<template>
  <Toast position="top-right" group="persistent" @close="handleClose" class="persistent-toast">
    <template #message="slotProps">
      <div class="persistent-toast-content">
        <div class="toast-header">
          <i :class="getIconClass(slotProps.message.severity)"></i>
          <span class="toast-summary">{{ slotProps.message.summary }}</span>
        </div>
        <div class="toast-body">
          <p class="toast-detail">{{ slotProps.message.detail }}</p>
        </div>
        <div class="toast-footer">
          <Button
            :label="t('common.dismiss')"
            icon="pi pi-times"
            class="p-button-text p-button-sm"
            @click="dismissWarning(slotProps.message)"
          />
          <small class="toast-timestamp">{{
            formatTimestamp(slotProps.message.data?.createdAt)
          }}</small>
        </div>
      </div>
    </template>
  </Toast>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import Toast from 'primevue/toast'
import Button from 'primevue/button'
import { useI18n } from 'vue-i18n'
import { usePersistentWarnings } from '@/composables/usePersistentWarnings'
import { formatLocaleDate } from '@/utils/dateFormat'

const { t } = useI18n()
const { removeWarning, displayStoredWarnings } = usePersistentWarnings()

const getIconClass = (severity: string) => {
  const iconMap: Record<string, string> = {
    warn: 'pi pi-exclamation-triangle',
    error: 'pi pi-times-circle',
    info: 'pi pi-info-circle',
    success: 'pi pi-check-circle',
  }
  return iconMap[severity] || 'pi pi-info-circle'
}

const formatTimestamp = (timestamp?: number) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return t('common.justNow')
  if (diffMins < 60) return t('common.minutesAgo', { count: diffMins })

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return t('common.hoursAgo', { count: diffHours })

  return formatLocaleDate(date)
}

const handleClose = (message: any) => {
  // Only remove from storage if it has a warningId
  if (message.data?.warningId) {
    removeWarning(message.data.warningId)
  }
}

const dismissWarning = (message: any) => {
  if (message.data?.warningId) {
    removeWarning(message.data.warningId)
  }
  // Close the toast programmatically
  message.visible = false
}

// Load and display warnings on mount
onMounted(() => {
  // Small delay to ensure Toast component is ready
  setTimeout(() => {
    displayStoredWarnings()
  }, 100)
})

// Listen for page visibility changes to redisplay warnings
const handleVisibilityChange = () => {
  if (!document.hidden) {
    displayStoredWarnings()
  }
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style scoped>
/* Base toast styles */
.persistent-toast :deep(.p-toast-message) {
  background: var(--app-surface-0, var(--app-surface-0, #ffffff));
  border-left: 4px solid;
  min-width: 350px;
  max-width: 450px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
}

/* Warning severity */
.persistent-toast :deep(.p-toast-message-warn) {
  border-left-color: var(--app-warning-color, var(--app-warning-color, #eab308));
  background: var(--app-warning-50, var(--app-warning-50, #fefce8));
}

.persistent-toast :deep(.p-toast-message-warn) .toast-header i {
  color: var(--app-warning-color, var(--app-warning-color, #ca8a04));
}

/* Error severity */
.persistent-toast :deep(.p-toast-message-error) {
  border-left-color: var(--app-danger-color, var(--app-danger-color, #ef4444));
  background: var(--app-danger-50, var(--app-danger-50, #fef2f2));
}

.persistent-toast :deep(.p-toast-message-error) .toast-header i {
  color: var(--app-danger-color, var(--app-danger-color, #dc2626));
}

/* Info severity */
.persistent-toast :deep(.p-toast-message-info) {
  border-left-color: var(--app-info-color);
  background: var(--app-info-50);
}

.persistent-toast :deep(.p-toast-message-info) .toast-header i {
  color: var(--app-info-text);
}

/* Success severity */
.persistent-toast :deep(.p-toast-message-success) {
  border-left-color: var(--app-success-color);
  background: var(--app-success-50);
}

.persistent-toast :deep(.p-toast-message-success) .toast-header i {
  color: var(--app-success-text);
}

/* Content layout */
.persistent-toast-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem;
}

.toast-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--app-text-primary, var(--app-text-primary, #1f2937));
}

.toast-header i {
  font-size: 1.25rem;
}

.toast-summary {
  font-size: 0.9375rem;
}

.toast-body {
  margin-left: 1.75rem;
}

.toast-detail {
  margin: 0;
  line-height: 1.5;
  font-size: 0.875rem;
  color: var(--app-text-primary-secondary, var(--app-text-secondary, #6b7280));
}

.toast-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-left: 1.75rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.toast-timestamp {
  color: var(--app-text-primary-secondary, var(--app-text-secondary, #9ca3af));
  font-size: 0.75rem;
  opacity: 0.8;
}

/* Dismiss button styling */
.persistent-toast :deep(.p-button-text) {
  font-size: 0.8125rem;
  padding: 0.375rem 0.75rem;
}

/* Dark mode support */
:global(.app-dark) .persistent-toast :deep(.p-toast-message),
:global([data-theme='dark']) .persistent-toast :deep(.p-toast-message) {
  background: var(--app-surface-800, var(--app-surface-800, #1f2937));
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

:global(.app-dark) .persistent-toast :deep(.p-toast-message-warn),
:global([data-theme='dark']) .persistent-toast :deep(.p-toast-message-warn) {
  background: rgba(251, 191, 36, 0.15);
  border-left-color: var(--app-warning-color, var(--app-warning-color, #facc15));
}

:global(.app-dark) .persistent-toast :deep(.p-toast-message-error),
:global([data-theme='dark']) .persistent-toast :deep(.p-toast-message-error) {
  background: rgba(239, 68, 68, 0.15);
  border-left-color: var(--app-danger-color, var(--app-danger-color, #f87171));
}

:global(.app-dark) .persistent-toast :deep(.p-toast-message-info),
:global([data-theme='dark']) .persistent-toast :deep(.p-toast-message-info) {
  background: rgba(59, 130, 246, 0.15);
  border-left-color: var(--app-info-color, var(--app-info-color, #60a5fa));
}

:global(.app-dark) .persistent-toast :deep(.p-toast-message-success),
:global([data-theme='dark']) .persistent-toast :deep(.p-toast-message-success) {
  background: rgba(34, 197, 94, 0.15);
  border-left-color: var(--app-success-color, var(--app-success-color, #4ade80));
}

:global(.app-dark) .toast-header,
:global([data-theme='dark']) .toast-header {
  color: var(--app-surface-100, var(--app-surface-100, #f3f4f6));
}

:global(.app-dark) .toast-detail,
:global([data-theme='dark']) .toast-detail {
  color: var(--app-surface-300, var(--app-surface-300, #d1d5db));
}

:global(.app-dark) .toast-timestamp,
:global([data-theme='dark']) .toast-timestamp {
  color: var(--app-surface-400, var(--app-surface-400, #9ca3af));
}

:global(.app-dark) .toast-footer,
:global([data-theme='dark']) .toast-footer {
  border-top-color: rgba(255, 255, 255, 0.1);
}

/* Animation for persistent toasts */
.persistent-toast :deep(.p-toast-message) {
  animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Sticky indicator */
.persistent-toast :deep(.p-toast-message)::before {
  content: '📌';
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 0.875rem;
  opacity: 0.6;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .persistent-toast :deep(.p-toast-message) {
    min-width: 280px;
    max-width: calc(100vw - 2rem);
  }

  .toast-footer {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
}

/* Hover effects */
.persistent-toast :deep(.p-toast-message):hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

:global(.app-dark) .persistent-toast :deep(.p-toast-message):hover,
:global([data-theme='dark']) .persistent-toast :deep(.p-toast-message):hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
}
</style>
